import { useState, useMemo, useCallback } from 'react';

/**
 * Hook for filtering items based on a search query
 * @template T The type of items to search through
 */
export interface UseSearchOptions<T> {
  /** Items to search through */
  items: T[];
  /** Function to determine if an item matches the search query */
  searchFn: (item: T, query: string) => boolean;
  /** Initial search query (default: '') */
  initialQuery?: string;
}

export interface UseSearchReturn<T> {
  /** Current search query */
  searchText: string;
  /** Function to update the search query */
  setSearchText: (query: string) => void;
  /** Filtered items based on search query */
  filteredItems: T[];
  /** Whether there is an active search */
  isSearching: boolean;
  /** Function to clear the search */
  clearSearch: () => void;
}

/**
 * Custom hook for searching/filtering items
 * Uses memoization to avoid re-computing filtered results unnecessarily
 * 
 * @example
 * ```tsx
 * const { searchText, setSearchText, filteredItems } = useSearch({
 *   items: users,
 *   searchFn: (user, query) => 
 *     user.email.toLowerCase().includes(query.toLowerCase())
 * });
 * ```
 */
export const useSearch = <T,>({
  items,
  searchFn,
  initialQuery = '',
}: UseSearchOptions<T>): UseSearchReturn<T> => {
  const [searchText, setSearchText] = useState(initialQuery);

  const filteredItems = useMemo(() => {
    const trimmedQuery = searchText.trim();
    
    if (!trimmedQuery) {
      return items;
    }

    const queryLower = trimmedQuery.toLowerCase();
    
    // Support multi-word search (AND logic)
    const searchTerms = queryLower.split(' ').filter(term => term.length > 0);
    
    return items.filter(item => {
      // All search terms must match
      return searchTerms.every(term => searchFn(item, term));
    });
  }, [items, searchText, searchFn]);

  const clearSearch = useCallback(() => {
    setSearchText('');
  }, []);

  const isSearching = searchText.trim().length > 0;

  return {
    searchText,
    setSearchText,
    filteredItems,
    isSearching,
    clearSearch,
  };
};
